import { useEffect, useRef } from 'react';

let modalBackSequence = 0;
const modalBackStack = [];
let suppressHistoryPop = false;
let suppressReleaseTimeout = null;

const releaseHistoryPopSuppression = () => {
  suppressHistoryPop = false;
  if (suppressReleaseTimeout) {
    clearTimeout(suppressReleaseTimeout);
    suppressReleaseTimeout = null;
  }
};

const beginHistoryPopSuppression = () => {
  suppressHistoryPop = true;

  const handleNextPopState = () => {
    window.removeEventListener('popstate', handleNextPopState);
    releaseHistoryPopSuppression();
  };

  window.addEventListener('popstate', handleNextPopState, { once: true });

  if (suppressReleaseTimeout) {
    clearTimeout(suppressReleaseTimeout);
  }

  // В некоторых PWA/webview popstate прилетает заметно позже.
  // Держим подавление чуть дольше, чтобы не схлопнуть родительский экран.
  suppressReleaseTimeout = setTimeout(() => {
    window.removeEventListener('popstate', handleNextPopState);
    releaseHistoryPopSuppression();
  }, 400);
};

const getTopModalId = () => modalBackStack[modalBackStack.length - 1] || null;
export const hasOpenModalBackEntries = () => modalBackStack.length > 0;

const removeModalId = (id) => {
  const index = modalBackStack.lastIndexOf(id);
  if (index >= 0) {
    modalBackStack.splice(index, 1);
  }
};

const useModalBackButton = (isOpen, onClose) => {
  const modalIdRef = useRef(null);
  const handledByPopRef = useRef(false);
  const onCloseRef = useRef(onClose);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    if (!isOpen) return;

    const modalId = ++modalBackSequence;
    modalIdRef.current = modalId;
    handledByPopRef.current = false;
    modalBackStack.push(modalId);

    const prev = parseInt(document.body.dataset.modalCount || '0', 10);
    document.body.dataset.modalCount = prev + 1;

    window.history.pushState(
      {
        ...(window.history.state || {}),
        __modalBackId: modalId,
      },
      '',
      window.location.href
    );

    const handleClose = () => {
      handledByPopRef.current = true;
      removeModalId(modalId);
      onCloseRef.current?.();
    };

    const handleKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      if (getTopModalId() !== modalId) return;
      event.preventDefault();
      handleClose();
    };

    const mountedAt = Date.now();
    const handlePopState = (event) => {
      if (suppressHistoryPop) return;
      // Защита от ложного немедленного срабатывания popstate в PWA/webview при создании записи истории
      if (Date.now() - mountedAt < 450) return;
      if (getTopModalId() !== modalId) return;
      const nextModalId = event.state?.__modalBackId ?? null;
      if (nextModalId === modalId) return;
      handleClose();
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('popstate', handlePopState);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('popstate', handlePopState);

      const current = parseInt(document.body.dataset.modalCount || '1', 10);
      document.body.dataset.modalCount = Math.max(0, current - 1);

      removeModalId(modalId);

      if (!handledByPopRef.current && window.history.state?.__modalBackId === modalId) {
        beginHistoryPopSuppression();
        window.history.back();
      }
    };
  }, [isOpen]);

  return onClose;
};

export default useModalBackButton;
